/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  CircularProgress,
} from "@mui/material";
import { Save, Edit } from "@mui/icons-material";
import {
  getAboutStatsSection,
  updateAboutStatsSection,
} from "actions/aboutStatsSection";
import {
  clearError,
  clearAboutStatsSection,
} from "features/aboutStatsSectionSlice";
import { RichTextEditor } from "components";
import { ADMIN_TEXTS } from "constants/adminTexts";
import "./About.scss";
import { BrandButton } from "components/brand";

const validationSchema = Yup.object({
  title: Yup.string()
    .required(ADMIN_TEXTS.aboutStatsSection.titleRequired)
    .max(255, ADMIN_TEXTS.aboutStatsSection.titleMaxLength),
  description: Yup.string()
    .required(ADMIN_TEXTS.aboutStatsSection.descriptionRequired)
    .test(
      "min-length",
      ADMIN_TEXTS.aboutStatsSection.descriptionMinLength,
      (value) => {
        const textContent = value?.replace(/<[^>]*>/g, "").trim();
        return textContent && textContent.length >= 10;
      },
    )
    .test(
      "max-length",
      ADMIN_TEXTS.aboutStatsSection.descriptionMaxLength,
      (value) => {
        const textContent = value?.replace(/<[^>]*>/g, "").trim();
        return !textContent || textContent.length <= 2000;
      },
    ),
});

export const AboutStatsSection = () => {
  const dispatch = useDispatch();
  const { aboutStatsSection, loading, updateLoading, error } = useSelector(
    (state) => state.aboutStatsSection,
  );
  const [isEditing, setIsEditing] = useState(false);

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
    },
    validationSchema,
    onSubmit: (values) => {
      dispatch(updateAboutStatsSection({ data: values }));
      setIsEditing(false);
    },
  });

  const handleCancel = () => {
    setIsEditing(false);
    formik.resetForm();
    if (aboutStatsSection) {
      formik.setFieldValue("title", aboutStatsSection.title || "");
      formik.setFieldValue("description", aboutStatsSection.description || "");
    }
  };

  useEffect(() => {
    dispatch(getAboutStatsSection());
    return () => {
      dispatch(clearAboutStatsSection());
    };
  }, []);

  useEffect(() => {
    if (aboutStatsSection) {
      formik.setFieldValue("title", aboutStatsSection.title || "");
      formik.setFieldValue("description", aboutStatsSection.description || "");
    }
  }, [aboutStatsSection]);

  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [error]);

  if (loading) {
    return (
      <Box className="loading-container">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="about-stats-section">
      <Box className="content-header">
        <Typography variant="h4" component="h1" fontWeight="bold">
          {ADMIN_TEXTS.aboutStatsSection.aboutStatsSection}
        </Typography>
        {!isEditing && (
          <BrandButton
            variant="primary"
            startIcon={<Edit />}
            onClick={() => setIsEditing(true)}
            className="edit-button"
          >
            {ADMIN_TEXTS.common.edit}
          </BrandButton>
        )}
      </Box>

      {isEditing ? (
        <Card>
          <CardContent>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={ADMIN_TEXTS.aboutStatsSection.titleAsterisk}
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    error={formik.touched.title && Boolean(formik.errors.title)}
                    helperText={formik.touched.title && formik.errors.title}
                    placeholder={ADMIN_TEXTS.aboutStatsSection.enterTitle}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RichTextEditor
                    value={formik.values.description}
                    onChange={(value) =>
                      formik.setFieldValue("description", value)
                    }
                    onBlur={() => formik.setFieldTouched("description", true)}
                    placeholder={ADMIN_TEXTS.aboutStatsSection.enterDescription}
                    label={ADMIN_TEXTS.aboutStatsSection.descriptionAsterisk}
                    required
                    error={formik.errors.description}
                    touched={formik.touched.description}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box className="form-actions">
                    <BrandButton
                      variant="secondary"
                      onClick={handleCancel}
                      disabled={updateLoading}
                    >
                      {ADMIN_TEXTS.common.cancel}
                    </BrandButton>
                    <BrandButton
                      type="submit"
                      variant="primary"
                      startIcon={<Save />}
                      disabled={updateLoading}
                    >
                      {updateLoading
                        ? ADMIN_TEXTS.actions.saving
                        : ADMIN_TEXTS.common.save}
                    </BrandButton>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              {aboutStatsSection?.title || ADMIN_TEXTS.aboutStatsSection.noData}
            </Typography>
            <Box
              className="description-content"
              dangerouslySetInnerHTML={{
                __html:
                  aboutStatsSection?.description ||
                  ADMIN_TEXTS.aboutStatsSection.noData,
              }}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
